/**
 * Admin · Aktions-Seite verwalten (`/admin/aktions-seite`)
 *
 * Zwei Bereiche in einer Seite:
 *  1. Betriebsmodus-Switch — steuert die öffentliche `/` (Editorial vs
 *     Aktion). Groß, deutlich, oben.
 *  2. Content-Editor — alle Texte, Zähler und Sektions-Inhalte der
 *     Aktions-Landing (`/aktion`).
 *
 * Datenmodell: `site_content` mit Keys `homepage_mode` und `aktion_page`
 * — genutzt von `useHomepageMode()` bzw. `useAktionsSeite()`.
 */
import { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import { Sparkles, Save, ExternalLink, Plus, Trash2, ArrowUp, ArrowDown, Upload } from "lucide-react";
import { api } from "@/lib/auth";
import WelcomePopupWidget from "@/components/admin/WelcomePopupWidget";
import {
    DEFAULT_AKTION_PAGE_CONFIG,
    type AktionPageConfig,
    type AktionComparisonSlot,
    type AktionComparisonItem,
    type AktionSpecialOffer,
    type GutscheinPopupConfig,
    type GutscheinCountdownMode,
    type WelcomeChoicePopupConfig,
    type ReferenzPopupConfig,
    type SpecialOfferStyle,
    type SpecialOfferFontFamily,
    type SpecialOfferFontWeight,
    type SpecialOfferTextSize,
    type VideoHeroConfig,
    type HeroChoiceTilesConfig,
    type HeroVideoTilesConfig,
    type VideoTileConfig,
} from "@/lib/useAktionsSeite";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL as string;

/** Kleiner Media-Doc-Typ aus /api/admin/media (nur die Felder, die wir brauchen). */
type MediaDoc = {
    id: string;
    title?: string;
    media_type: string;   // "image" | "video" | "photo_360" | "video_360" | …
    poster_url?: string;
};

/** Editor für einen `AktionComparisonSlot`. Inline, kein separates Modal. */
function ComparisonSlotEditor({
    label,
    slot,
    onChange,
    testIdPrefix,
    mediaLibrary,
}: {
    label: string;
    slot: AktionComparisonSlot;
    onChange: (next: AktionComparisonSlot) => void;
    testIdPrefix: string;
    mediaLibrary: MediaDoc[];
}) {
    const [uploadingIdx, setUploadingIdx] = useState<number | null>(null);
    const items = slot.items || [];

    const setItem = (i: number, next: AktionComparisonItem) => {
        const copy = [...items];
        copy[i] = next;
        onChange({ ...slot, items: copy });
    };
    const removeItem = (i: number) => onChange({ ...slot, items: items.filter((_, idx) => idx !== i) });
    const move = (i: number, dir: -1 | 1) => {
        const j = i + dir;
        if (j < 0 || j >= items.length) return;
        const copy = [...items];
        [copy[i], copy[j]] = [copy[j], copy[i]];
        onChange({ ...slot, items: copy });
    };
    const addItem = () => onChange({ ...slot, items: [...items, { media_type: "image", src: "" }] });

    const uploadFile = async (file: File, i: number) => {
        setUploadingIdx(i);
        try {
            const fd = new FormData();
            fd.append("file", file);
            // Media-Type-Hint aus MIME; 360°-Typen kann der User im Select manuell setzen.
            fd.append("media_type", file.type.startsWith("video/") ? "video" : "image");
            const res = await fetch(`${BACKEND_URL}/api/admin/media`, {
                method: "POST",
                credentials: "include",
                body: fd,
            });
            if (!res.ok) {
                const t = await res.text();
                toast.error(`Upload fehlgeschlagen (HTTP ${res.status}): ${t.slice(0, 200)}`);
                return;
            }
            const doc = (await res.json()) as MediaDoc;
            const mt = (items[i]?.media_type) || (file.type.startsWith("video/") ? "video" : "image");
            setItem(i, {
                media_type: mt,
                media_id: doc.id,
                src: `${BACKEND_URL}/api/media/${doc.id}/file`,
                poster: items[i]?.poster,
            });
            toast.success("Datei hochgeladen und in die Kachel übernommen.");
        } catch (e) {
            toast.error(`Upload-Fehler: ${(e as Error).message}`);
        } finally {
            setUploadingIdx(null);
        }
    };

    const inputCls = "w-full px-2 py-1.5 rounded-sm bg-white text-sm";
    const borderStyle = { border: "1px solid hsl(var(--hf-line))" };

    return (
        <div className="p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid={`${testIdPrefix}-editor`}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">{label}</div>
                <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs bg-white"
                    style={borderStyle}
                    data-testid={`${testIdPrefix}-add`}
                >
                    <Plus size={14} /> Medium hinzufügen
                </button>
            </div>

            {/* Slideshow-Toggle + Intervall */}
            <div className="flex flex-wrap items-center gap-4 mb-4 p-3 rounded-sm bg-white" style={borderStyle}>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={slot.slideshow_enabled}
                        onChange={(e) => onChange({ ...slot, slideshow_enabled: e.target.checked })}
                        data-testid={`${testIdPrefix}-slideshow-toggle`}
                    />
                    <span>Als Slideshow anzeigen</span>
                </label>
                <label className={`flex items-center gap-2 text-sm ${!slot.slideshow_enabled ? "opacity-50" : ""}`}>
                    <span>Wechsel alle</span>
                    <input
                        type="number"
                        min={1}
                        max={30}
                        step={1}
                        value={slot.interval_seconds}
                        onChange={(e) => onChange({ ...slot, interval_seconds: Math.max(1, Math.min(30, parseInt(e.target.value) || 3)) })}
                        disabled={!slot.slideshow_enabled}
                        className="w-16 px-2 py-1 rounded-sm bg-white text-sm text-center"
                        style={borderStyle}
                        data-testid={`${testIdPrefix}-interval`}
                    />
                    <span>Sekunden</span>
                </label>
            </div>

            {/* Item-Liste */}
            <div className="grid gap-3">
                {items.map((it, i) => (
                    <div key={i} className="p-3 rounded-sm bg-white" style={borderStyle} data-testid={`${testIdPrefix}-item-${i}`}>
                        <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Medium {i + 1}</div>
                            <div className="flex items-center gap-1">
                                <button type="button" aria-label="hoch" disabled={i === 0} onClick={() => move(i, -1)} className="p-1 rounded-sm" style={{ opacity: i === 0 ? 0.35 : 1 }}><ArrowUp size={14} /></button>
                                <button type="button" aria-label="runter" disabled={i === items.length - 1} onClick={() => move(i, 1)} className="p-1 rounded-sm" style={{ opacity: i === items.length - 1 ? 0.35 : 1 }}><ArrowDown size={14} /></button>
                                <button type="button" aria-label="entfernen" onClick={() => removeItem(i)} className="p-1 rounded-sm" style={{ color: "#B91C1C" }}><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <div className="grid gap-2">
                            <div className="grid md:grid-cols-[140px_1fr] gap-2 items-center">
                                <label className="text-xs text-hf-muted">Medien-Typ</label>
                                <select
                                    value={it.media_type}
                                    onChange={(e) => setItem(i, { ...it, media_type: e.target.value as AktionComparisonItem["media_type"] })}
                                    className={inputCls}
                                    style={borderStyle}
                                    data-testid={`${testIdPrefix}-item-${i}-type`}
                                >
                                    <option value="image">Foto</option>
                                    <option value="video">Video</option>
                                    <option value="photo_360">360°-Foto</option>
                                    <option value="video_360">360°-Video</option>
                                </select>
                            </div>
                            <div className="grid md:grid-cols-[140px_1fr] gap-2 items-center">
                                <label className="text-xs text-hf-muted">URL / Pfad</label>
                                <input
                                    value={it.src}
                                    onChange={(e) => setItem(i, { ...it, src: e.target.value })}
                                    placeholder="/media/aktion/… oder https://…"
                                    className={inputCls}
                                    style={borderStyle}
                                    data-testid={`${testIdPrefix}-item-${i}-src`}
                                />
                            </div>
                            <div className="grid md:grid-cols-[140px_1fr] gap-2 items-center">
                                <label className="text-xs text-hf-muted">Media-ID (optional)</label>
                                <div className="flex gap-2">
                                    <input
                                        value={it.media_id || ""}
                                        onChange={(e) => setItem(i, { ...it, media_id: e.target.value })}
                                        placeholder="aus Mediathek: UUID …"
                                        className={inputCls}
                                        style={borderStyle}
                                        data-testid={`${testIdPrefix}-item-${i}-media-id`}
                                    />
                                    <select
                                        value=""
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            if (!id) return;
                                            const doc = mediaLibrary.find((m) => m.id === id);
                                            if (!doc) return;
                                            setItem(i, {
                                                ...it,
                                                media_id: id,
                                                src: `${BACKEND_URL}/api/media/${id}/file`,
                                                media_type: (["image", "video", "photo_360", "video_360"].includes(doc.media_type)
                                                    ? doc.media_type
                                                    : it.media_type) as AktionComparisonItem["media_type"],
                                            });
                                        }}
                                        className="w-[180px] px-2 py-1.5 rounded-sm bg-white text-xs"
                                        style={borderStyle}
                                        data-testid={`${testIdPrefix}-item-${i}-picker`}
                                    >
                                        <option value="">— Aus Mediathek —</option>
                                        {mediaLibrary.slice(0, 200).map((m) => (
                                            <option key={m.id} value={m.id}>
                                                {(m.title || m.id.slice(0, 8))} [{m.media_type}]
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            {(it.media_type === "video" || it.media_type === "video_360") && (
                                <div className="grid md:grid-cols-[140px_1fr] gap-2 items-center">
                                    <label className="text-xs text-hf-muted">Poster (optional)</label>
                                    <input
                                        value={it.poster || ""}
                                        onChange={(e) => setItem(i, { ...it, poster: e.target.value })}
                                        placeholder="/media/…"
                                        className={inputCls}
                                        style={borderStyle}
                                    />
                                </div>
                            )}
                            <div className="flex items-center gap-2 pt-1">
                                <label
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs cursor-pointer bg-white"
                                    style={borderStyle}
                                >
                                    <Upload size={12} />
                                    {uploadingIdx === i ? "Wird hochgeladen…" : "Datei hochladen"}
                                    <input
                                        type="file"
                                        accept="image/*,video/*"
                                        className="hidden"
                                        onChange={(e) => {
                                            const f = e.target.files?.[0];
                                            if (f) uploadFile(f, i);
                                            e.currentTarget.value = "";
                                        }}
                                        data-testid={`${testIdPrefix}-item-${i}-upload`}
                                    />
                                </label>
                                {it.src && (
                                    <a
                                        href={it.src}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs underline"
                                        style={{ color: "#8A6A2C" }}
                                    >
                                        Vorschau öffnen
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
                {items.length === 0 && (
                    <div className="text-sm text-hf-muted italic p-3">
                        Noch kein Medium — klicke oben rechts auf &bdquo;Medium hinzuf&uuml;gen&ldquo;.
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Kompakter Typografie-Editor für ein Special-Offer-Text-Element.
 * Kombiniert Größe, Farbe, Schriftart, Fettung und Uppercase in einer
 * grauen Sub-Zeile pro Text. Der Style wird an die Special-Offer-Sektion
 * als inline CSS durchgereicht.
 */
function SpecialOfferStyleEditor({
    label,
    style,
    onChange,
    testIdPrefix,
}: {
    label: string;
    style: SpecialOfferStyle;
    onChange: (next: SpecialOfferStyle) => void;
    testIdPrefix: string;
}) {
    const inputCls = "w-full px-2 py-1.5 rounded-sm bg-white text-sm";
    const borderStyle = { border: "1px solid hsl(var(--hf-line))" };
    const labelCls = "block text-[10px] uppercase tracking-[0.16em] text-hf-muted mb-1";
    return (
        <div className="p-3 rounded-sm bg-white" style={borderStyle} data-testid={`${testIdPrefix}-style`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-2">{label} · Typografie</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div>
                    <label className={labelCls}>Größe</label>
                    <select
                        value={style.size}
                        onChange={(e) => onChange({ ...style, size: e.target.value as SpecialOfferTextSize })}
                        className={inputCls}
                        style={borderStyle}
                        data-testid={`${testIdPrefix}-size`}
                    >
                        <option value="sm">SM (16 px)</option>
                        <option value="md">MD (20 px)</option>
                        <option value="lg">LG (24 px)</option>
                        <option value="xl">XL (36 px)</option>
                        <option value="2xl">2XL (48 px)</option>
                        <option value="3xl">3XL (72 px)</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Schriftart</label>
                    <select
                        value={style.font_family}
                        onChange={(e) => onChange({ ...style, font_family: e.target.value as SpecialOfferFontFamily })}
                        className={inputCls}
                        style={borderStyle}
                        data-testid={`${testIdPrefix}-family`}
                    >
                        <option value="sans">Sans (Inter)</option>
                        <option value="serif">Serif (Cormorant)</option>
                        <option value="display">Display (Cormorant)</option>
                        <option value="anton">Anton</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Fettung</label>
                    <select
                        value={style.weight}
                        onChange={(e) => onChange({ ...style, weight: e.target.value as SpecialOfferFontWeight })}
                        className={inputCls}
                        style={borderStyle}
                        data-testid={`${testIdPrefix}-weight`}
                    >
                        <option value="normal">Normal</option>
                        <option value="bold">Bold</option>
                        <option value="black">Black</option>
                    </select>
                </div>
                <div>
                    <label className={labelCls}>Farbe</label>
                    <div className="flex gap-1">
                        <input
                            type="color"
                            value={style.color || "#0F1830"}
                            onChange={(e) => onChange({ ...style, color: e.target.value })}
                            className="w-10 h-8 rounded-sm cursor-pointer"
                            style={borderStyle}
                            data-testid={`${testIdPrefix}-color-picker`}
                        />
                        <input
                            type="text"
                            value={style.color || ""}
                            onChange={(e) => onChange({ ...style, color: e.target.value })}
                            className={inputCls + " flex-1"}
                            style={borderStyle}
                            placeholder="#0F1830"
                            data-testid={`${testIdPrefix}-color-text`}
                        />
                    </div>
                </div>
                <div className="flex items-end">
                    <label className="inline-flex items-center gap-2 text-sm text-hf-ink cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!style.uppercase}
                            onChange={(e) => onChange({ ...style, uppercase: e.target.checked })}
                            data-testid={`${testIdPrefix}-uppercase`}
                        />
                        GROSS
                    </label>
                </div>
            </div>
        </div>
    );
}



/**
 * Editor-Block für das Video-Hero (Kopf-Element von `/aktion`).
 * Kompakt: Master-Toggle, beide Video-URLs, Poster, Verhaltens-Toggles,
 * kleine Live-Vorschau.
 */
function VideoHeroEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: VideoHeroConfig;
    onChange: (next: VideoHeroConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<VideoHeroConfig>) => onChange({ ...cfg, ...patch });
    return (
        <section
            className="mb-6 p-5 rounded-md"
            style={{
                background: cfg.enabled
                    ? "linear-gradient(180deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-bg)) 100%)"
                    : "hsl(var(--hf-surface))",
                border: `1.5px solid ${cfg.enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))"}`,
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid="aktion-cfg-video-hero"
        >
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                        Video-Hero (ganz oben auf /aktion)
                    </div>
                    <p className="text-xs text-hf-muted mt-1 max-w-2xl">
                        Erscheint über dem AUFGEPASST-Hero. Bei <strong>Aus</strong>: bestehendes
                        Layout bleibt unverändert. Optimaler Video-Umfang:
                        <strong> unter 8 MB pro Version, H.264, Faststart.</strong>
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="aktion-cfg-vh-enabled"
                    />
                    <span className="font-medium">{cfg.enabled ? "Video-Hero aktiv" : "Video-Hero aus"}</span>
                </label>
            </div>

            <div className={cfg.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                {/* URLs */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div>
                        <label className={labelCls}>Desktop-Video-URL (21:9)</label>
                        <input
                            type="url"
                            value={cfg.video_desktop_url}
                            onChange={(e) => upd({ video_desktop_url: e.target.value })}
                            className={inputCls} style={borderStyle}
                            placeholder="https://…/video-21x9.mp4"
                            data-testid="aktion-cfg-vh-desktop-url"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">
                            Wird ab 768 px Bildschirmbreite geladen. Ideal 1920×822.
                        </p>
                    </div>
                    <div>
                        <label className={labelCls}>Mobile-Video-URL (9:16)</label>
                        <input
                            type="url"
                            value={cfg.video_mobile_url}
                            onChange={(e) => upd({ video_mobile_url: e.target.value })}
                            className={inputCls} style={borderStyle}
                            placeholder="https://…/video-9x16.mp4"
                            data-testid="aktion-cfg-vh-mobile-url"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">
                            Wird unter 768 px + als Fallback für leeres Desktop-Feld genutzt.
                            Ideal 1080×1920.
                        </p>
                    </div>
                    <div>
                        <label className={labelCls}>Poster-Bild-URL (optional)</label>
                        <input
                            type="url"
                            value={cfg.poster_url}
                            onChange={(e) => upd({ poster_url: e.target.value })}
                            className={inputCls} style={borderStyle}
                            placeholder="https://…/poster.jpg"
                            data-testid="aktion-cfg-vh-poster-url"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">
                            Wird gezeigt, solange das Video lädt. Leer lassen für Standard-Ladeverhalten.
                        </p>
                    </div>
                </div>

                {/* Verhalten */}
                <div className="grid gap-3 md:grid-cols-2 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!cfg.loop}
                            onChange={(e) => upd({ loop: e.target.checked })}
                            className="w-4 h-4"
                            data-testid="aktion-cfg-vh-loop"
                        />
                        <span>Video im Loop wiedergeben</span>
                    </label>
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!cfg.show_sound_button}
                            onChange={(e) => upd({ show_sound_button: e.target.checked })}
                            className="w-4 h-4"
                            data-testid="aktion-cfg-vh-sound-btn"
                        />
                        <span>&bdquo;Mit Ton&ldquo;-Button anzeigen</span>
                    </label>
                </div>

                {/* Beschriftungen */}
                <div className="grid gap-3 md:grid-cols-2 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div>
                        <label className={labelCls}>Ton-Button-Text</label>
                        <input
                            value={cfg.sound_button_text}
                            onChange={(e) => upd({ sound_button_text: e.target.value })}
                            className={inputCls} style={borderStyle}
                            data-testid="aktion-cfg-vh-sound-text"
                        />
                    </div>
                    <div>
                        <label className={labelCls}>Stumm-Indikator (oben links)</label>
                        <input
                            value={cfg.muted_label}
                            onChange={(e) => upd({ muted_label: e.target.value })}
                            className={inputCls} style={borderStyle}
                            data-testid="aktion-cfg-vh-muted-label"
                        />
                    </div>
                </div>

                {/* Live-Vorschau */}
                {(cfg.video_desktop_url || cfg.video_mobile_url) && (
                    <div className="p-3 rounded-sm bg-black" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted mb-2" style={{ color: "#F5E6C8" }}>
                            Live-Vorschau (stumm)
                        </div>
                        <div style={{ maxWidth: 480, aspectRatio: "21 / 9", margin: "0 auto" }}>
                            <video
                                key={cfg.video_desktop_url + "|" + cfg.video_mobile_url}
                                src={cfg.video_desktop_url || cfg.video_mobile_url}
                                autoPlay
                                muted
                                loop
                                playsInline
                                className="w-full h-full object-cover rounded-sm"
                                data-testid="aktion-cfg-vh-preview"
                            />
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}


/**
 * Editor-Block für die permanenten Auswahl-Kacheln neben dem Hero-CTA
 * (100 % Kostenlos / 80 % Neukunden-Rabatt). Ersetzt seit dem Redesign
 * das auto-öffnende Welcome-Choice-Popup.
 */
function HeroChoiceTilesEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: HeroChoiceTilesConfig;
    onChange: (next: HeroChoiceTilesConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<HeroChoiceTilesConfig>) => onChange({ ...cfg, ...patch });
    return (
        <section
            className="mb-6 p-5 rounded-md"
            style={{
                background: cfg.enabled
                    ? "linear-gradient(180deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-bg)) 100%)"
                    : "hsl(var(--hf-surface))",
                border: `1.5px solid ${cfg.enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))"}`,
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid="aktion-cfg-hero-choice-tiles"
        >
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                        Auswahl-Kacheln (Hero)
                    </div>
                    <p className="text-xs text-hf-muted mt-1 max-w-2xl">
                        Zwei permanent sichtbare Kacheln neben dem &bdquo;Infos erhalten&ldquo;-CTA.
                        Klick → Referenz- bzw. Rabatt-Formular öffnet sich direkt.
                        Bei <strong>Aus</strong>: Kacheln unsichtbar, Layout klappt zurück.
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="aktion-cfg-hct-enabled"
                    />
                    <span className="font-medium">{cfg.enabled ? "Kacheln aktiv" : "Kacheln aus"}</span>
                </label>
            </div>

            <div className={cfg.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                <div className="grid gap-3 md:grid-cols-2">
                    {/* Kachel 1 · Referenz */}
                    <div className="p-3 rounded-sm bg-white space-y-2" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                            Kachel 1 · Referenz (Gold)
                        </div>
                        <div>
                            <label className={labelCls}>Titel</label>
                            <input
                                value={cfg.card_referenz_title}
                                onChange={(e) => upd({ card_referenz_title: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-ref-title"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Untertext (italic)</label>
                            <input
                                value={cfg.card_referenz_subline}
                                onChange={(e) => upd({ card_referenz_subline: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-ref-subline"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>CTA-Text</label>
                            <input
                                value={cfg.card_referenz_cta}
                                onChange={(e) => upd({ card_referenz_cta: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-ref-cta"
                            />
                        </div>
                    </div>
                    {/* Kachel 2 · Rabatt */}
                    <div className="p-3 rounded-sm bg-white space-y-2" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                            Kachel 2 · Rabatt (Rot)
                        </div>
                        <div>
                            <label className={labelCls}>Titel</label>
                            <input
                                value={cfg.card_rabatt_title}
                                onChange={(e) => upd({ card_rabatt_title: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-rab-title"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Untertext (italic)</label>
                            <input
                                value={cfg.card_rabatt_subline}
                                onChange={(e) => upd({ card_rabatt_subline: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-rab-subline"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>CTA-Text</label>
                            <input
                                value={cfg.card_rabatt_cta}
                                onChange={(e) => upd({ card_rabatt_cta: e.target.value })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-hct-rab-cta"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


/**
 * Editor-Block für die zwei Video-Teaser-Kacheln neben den
 * `HeroChoiceTiles`. Master-Toggle + pro Kachel eigenständige Felder
 * (enabled, Titel, 16:9- und 21:9-URL, Preferred-Aspect). Live-
 * Vorschau (stumm, autoplay, loop) direkt unter jeder Kachel — 1:1
 * so, wie sie später im Hero aussieht.
 */
function HeroVideoTilesEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: HeroVideoTilesConfig;
    onChange: (next: HeroVideoTilesConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<HeroVideoTilesConfig>) => onChange({ ...cfg, ...patch });
    const updTile = (slot: "tile_1" | "tile_2", patch: Partial<VideoTileConfig>) =>
        onChange({ ...cfg, [slot]: { ...cfg[slot], ...patch } });

    const renderTile = (slot: "tile_1" | "tile_2", label: string) => {
        const tile = cfg[slot];
        const src = tile.preferred_aspect === "21:9"
            ? (tile.video_21x9_url || tile.video_16x9_url)
            : (tile.video_16x9_url || tile.video_21x9_url);
        const aspect = tile.preferred_aspect === "21:9" ? "21 / 9" : "16 / 9";
        return (
            <div className="p-3 rounded-sm bg-white space-y-2" style={borderStyle}>
                <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                        {label}
                    </div>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                        <input
                            type="checkbox"
                            checked={!!tile.enabled}
                            onChange={(e) => updTile(slot, { enabled: e.target.checked })}
                            className="w-4 h-4"
                            data-testid={`aktion-cfg-hvt-${slot}-enabled`}
                        />
                        <span>{tile.enabled ? "aktiv" : "aus"}</span>
                    </label>
                </div>
                <div>
                    <label className={labelCls}>Titel</label>
                    <input
                        value={tile.title}
                        onChange={(e) => updTile(slot, { title: e.target.value })}
                        className={inputCls} style={borderStyle}
                        data-testid={`aktion-cfg-hvt-${slot}-title`}
                    />
                </div>
                <div>
                    <label className={labelCls}>Video-URL 16:9</label>
                    <input
                        type="url"
                        value={tile.video_16x9_url}
                        onChange={(e) => updTile(slot, { video_16x9_url: e.target.value })}
                        className={inputCls} style={borderStyle}
                        placeholder="https://…/video-16x9.mp4"
                        data-testid={`aktion-cfg-hvt-${slot}-url-16x9`}
                    />
                </div>
                <div>
                    <label className={labelCls}>Video-URL 21:9</label>
                    <input
                        type="url"
                        value={tile.video_21x9_url}
                        onChange={(e) => updTile(slot, { video_21x9_url: e.target.value })}
                        className={inputCls} style={borderStyle}
                        placeholder="https://…/video-21x9.mp4"
                        data-testid={`aktion-cfg-hvt-${slot}-url-21x9`}
                    />
                </div>
                <div>
                    <label className={labelCls}>Bevorzugtes Seitenverhältnis</label>
                    <select
                        value={tile.preferred_aspect}
                        onChange={(e) => updTile(slot, { preferred_aspect: e.target.value as "16:9" | "21:9" })}
                        className={inputCls} style={borderStyle}
                        data-testid={`aktion-cfg-hvt-${slot}-aspect`}
                    >
                        <option value="16:9">16:9 (Standard)</option>
                        <option value="21:9">21:9 (Cinema)</option>
                    </select>
                    <p className="text-[10px] text-hf-muted mt-1">
                        Bei fehlender URL wird automatisch auf die andere Variante zurückgegriffen.
                    </p>
                </div>
                {src ? (
                    <div className="pt-2">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted mb-1">
                            Vorschau (stumm)
                        </div>
                        <div
                            className="relative w-full overflow-hidden rounded-sm"
                            style={{ aspectRatio: aspect, background: "#0F1830", maxWidth: 320 }}
                        >
                            <video
                                key={src}
                                src={src}
                                autoPlay
                                muted
                                loop
                                playsInline
                                preload="metadata"
                                className="absolute inset-0 w-full h-full object-cover"
                                data-testid={`aktion-cfg-hvt-${slot}-preview`}
                            />
                        </div>
                    </div>
                ) : null}
            </div>
        );
    };

    return (
        <section
            className="mb-6 p-5 rounded-md"
            style={{
                background: cfg.enabled
                    ? "linear-gradient(180deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-bg)) 100%)"
                    : "hsl(var(--hf-surface))",
                border: `1.5px solid ${cfg.enabled ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))"}`,
                boxShadow: "var(--shadow-warm-sm)",
            }}
            data-testid="aktion-cfg-hero-video-tiles"
        >
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                        Video-Kacheln (Hero)
                    </div>
                    <p className="text-xs text-hf-muted mt-1 max-w-2xl">
                        Zwei zusätzliche Video-Teaser neben den Auswahl-Kacheln.
                        Klick → Fullscreen-Modal mit Ton. Vorschau in der Kachel
                        läuft stumm im Loop. Bei <strong>Aus</strong>: beide
                        Kacheln unsichtbar, Layout klappt zurück auf 2 Kacheln.
                    </p>
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="aktion-cfg-hvt-enabled"
                    />
                    <span className="font-medium">{cfg.enabled ? "Kacheln aktiv" : "Kacheln aus"}</span>
                </label>
            </div>

            <div className={cfg.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                <div className="grid gap-3 md:grid-cols-2">
                    {renderTile("tile_1", "Kachel 1")}
                    {renderTile("tile_2", "Kachel 2")}
                </div>
            </div>
        </section>
    );
}


/**
 * Kompakter Editor-Block für das Gutschein-Popup. Kapselt alle Felder
 * (Trigger, Texte, Countdown, Trust-Badges, Formular) und hält das JSX
 * in `AdminAktionsSeite` schlank.
 */
function GutscheinPopupEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: GutscheinPopupConfig;
    onChange: (next: GutscheinPopupConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<GutscheinPopupConfig>) => onChange({ ...cfg, ...patch });
    const badges = cfg.trust_badges || [];
    const setBadge = (i: number, v: string) => {
        const copy = [...badges];
        copy[i] = v;
        upd({ trust_badges: copy });
    };
    const addBadge = () => upd({ trust_badges: [...badges, ""] });
    const removeBadge = (i: number) => upd({ trust_badges: badges.filter((_, idx) => idx !== i) });

    return (
        <div
            className="grid gap-4 mb-6 p-5 rounded-sm"
            style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}
            data-testid="aktion-cfg-gutschein-popup"
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Gutschein-Popup (Promo-Modal)</div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="aktion-cfg-gp-enabled"
                    />
                    <span className="font-medium">{cfg.enabled ? "Popup aktiv" : "Popup ausgeblendet"}</span>
                </label>
            </div>

            <div className={cfg.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                {/* Trigger-Sub-Block */}
                <div className="grid gap-3 md:grid-cols-2 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div>
                        <label className={labelCls}>Trigger-Sekunden (0 = deaktiviert)</label>
                        <input
                            type="number" min={0} max={600}
                            value={cfg.trigger_seconds}
                            onChange={(e) => upd({ trigger_seconds: Math.max(0, parseInt(e.target.value) || 0) })}
                            className={inputCls} style={borderStyle}
                            data-testid="aktion-cfg-gp-trigger-seconds"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">Popup öffnet nach so vielen Sekunden auf der Seite.</p>
                    </div>
                    <div>
                        <label className={labelCls}>Scroll-% (0 = deaktiviert)</label>
                        <input
                            type="number" min={0} max={100}
                            value={cfg.trigger_scroll_percent}
                            onChange={(e) => upd({ trigger_scroll_percent: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                            className={inputCls} style={borderStyle}
                            data-testid="aktion-cfg-gp-trigger-scroll"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">Öffnet, sobald der User so viel % gescrollt hat. Was zuerst greift, gewinnt.</p>
                    </div>
                </div>

                {/* Text-Sub-Block */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>Kicker (klein oben)</label>
                            <input value={cfg.kicker} onChange={(e) => upd({ kicker: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-kicker" />
                        </div>
                        <div>
                            <label className={labelCls}>Headline</label>
                            <input value={cfg.headline} onChange={(e) => upd({ headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-headline" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className={labelCls}>Rabatt-Prozent (Zahl)</label>
                            <input
                                type="number" min={0} max={100}
                                value={cfg.discount_percent}
                                onChange={(e) => upd({ discount_percent: Math.max(0, Math.min(100, parseInt(e.target.value) || 0)) })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-gp-discount"
                            />
                        </div>
                        <div>
                            <label className={labelCls}>Label im Stern</label>
                            <input value={cfg.discount_label} onChange={(e) => upd({ discount_label: e.target.value })} className={inputCls} style={borderStyle} placeholder="RABATT" data-testid="aktion-cfg-gp-discount-label" />
                        </div>
                        <div>
                            <label className={labelCls}>Kampagnen-Tag (Analytics)</label>
                            <input value={cfg.campaign_tag} onChange={(e) => upd({ campaign_tag: e.target.value })} className={inputCls} style={borderStyle} placeholder="gutschein_80" data-testid="aktion-cfg-gp-campaign" />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Body-Text unter dem Stern</label>
                        <input value={cfg.body} onChange={(e) => upd({ body: e.target.value })} className={inputCls} style={borderStyle} placeholder="auf ALLE Leistungen" data-testid="aktion-cfg-gp-body" />
                    </div>
                    <div>
                        <label className={labelCls}>Feindruck (Gültigkeit)</label>
                        <input value={cfg.fine_print} onChange={(e) => upd({ fine_print: e.target.value })} className={inputCls} style={borderStyle} placeholder="Rabatt nur heute buchbar · 4 Wochen einlösbar" data-testid="aktion-cfg-gp-fineprint" />
                    </div>
                </div>

                {/* Countdown-Sub-Block */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Countdown</div>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div>
                            <label className={labelCls}>Modus</label>
                            <select
                                value={cfg.countdown_mode}
                                onChange={(e) => upd({ countdown_mode: e.target.value as GutscheinCountdownMode })}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-gp-cd-mode"
                            >
                                <option value="midnight">Mitternacht heute (rolling)</option>
                                <option value="fixed">Fixes Datum</option>
                                <option value="off">Aus</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Fixes Zieldatum {cfg.countdown_mode !== "fixed" ? "(nur bei Modus = Fixes Datum)" : ""}</label>
                            <input
                                type="datetime-local"
                                value={cfg.countdown_target_iso ? cfg.countdown_target_iso.slice(0, 16) : ""}
                                onChange={(e) => upd({ countdown_target_iso: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                                disabled={cfg.countdown_mode !== "fixed"}
                                className={inputCls} style={borderStyle}
                                data-testid="aktion-cfg-gp-cd-target"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className={labelCls}>Prefix</label>
                                <input value={cfg.countdown_prefix} onChange={(e) => upd({ countdown_prefix: e.target.value })} className={inputCls} style={borderStyle} placeholder="noch" data-testid="aktion-cfg-gp-cd-prefix" />
                            </div>
                            <div>
                                <label className={labelCls}>Suffix</label>
                                <input value={cfg.countdown_suffix} onChange={(e) => upd({ countdown_suffix: e.target.value })} className={inputCls} style={borderStyle} placeholder="Std" data-testid="aktion-cfg-gp-cd-suffix" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Trust-Badges */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Trust-Badges (max. 4, 2 pro Zeile)</div>
                        <button type="button" onClick={addBadge} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs" style={{ background: "#F8FAFC", ...borderStyle }} data-testid="aktion-cfg-gp-badge-add" disabled={badges.length >= 4}>
                            <Plus size={14} /> Badge hinzufügen
                        </button>
                    </div>
                    {badges.map((b, i) => (
                        <div key={i} className="flex items-center gap-2">
                            <input
                                value={b}
                                onChange={(e) => setBadge(i, e.target.value)}
                                className={inputCls + " flex-1"} style={borderStyle}
                                placeholder="🛡 UNVERBINDLICH RESERVIEREN"
                                data-testid={`aktion-cfg-gp-badge-${i}`}
                            />
                            <button type="button" onClick={() => removeBadge(i)} className="p-1.5 rounded-sm" style={{ color: "#B91C1C" }} aria-label="Entfernen">
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                    {badges.length === 0 && (
                        <p className="text-xs text-hf-muted italic">Noch keine Trust-Badges — mit &bdquo;+&ldquo; hinzuf&uuml;gen.</p>
                    )}
                </div>

                {/* CTA / Dismiss / Footer */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>CTA-Button-Text</label>
                            <input value={cfg.cta_text} onChange={(e) => upd({ cta_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-cta" />
                        </div>
                        <div>
                            <label className={labelCls}>Reassurance-Zeile (unter CTA)</label>
                            <input value={cfg.reassurance_line} onChange={(e) => upd({ reassurance_line: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-reassurance" />
                        </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                        <div>
                            <label className={labelCls}>&bdquo;Sp&auml;ter erinnern&ldquo;-Text</label>
                            <input value={cfg.dismiss_text} onChange={(e) => upd({ dismiss_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-dismiss" />
                        </div>
                        <div>
                            <label className={labelCls}>Footer-Text (ganz unten, klein)</label>
                            <input value={cfg.footer_text} onChange={(e) => upd({ footer_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-footer" />
                        </div>
                    </div>
                </div>

                {/* Formular-Sub-Block (kollabierbar) */}
                <details className="p-3 rounded-sm bg-white" style={borderStyle} open>
                    <summary className="cursor-pointer text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                        Mini-Formular & Vertrauens-Texte im Popup
                    </summary>
                    <div className="mt-3 grid gap-3">
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={!!cfg.form_enabled}
                                onChange={(e) => upd({ form_enabled: e.target.checked })}
                                data-testid="aktion-cfg-gp-form-enabled"
                            />
                            <span>Formular im Popup anzeigen (aus → CTA scrollt zum Haupt-Formular)</span>
                        </label>
                        <div className={cfg.form_enabled ? "grid gap-3" : "grid gap-3 opacity-40 pointer-events-none"}>
                            <div>
                                <label className={labelCls}>Formular-Headline</label>
                                <input value={cfg.form_headline} onChange={(e) => upd({ form_headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-form-headline" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Name-Label</label>
                                    <input value={cfg.form_name_label} onChange={(e) => upd({ form_name_label: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>Name-Placeholder</label>
                                    <input value={cfg.form_name_placeholder} onChange={(e) => upd({ form_name_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>Telefon-Label</label>
                                    <input value={cfg.form_phone_label} onChange={(e) => upd({ form_phone_label: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>Telefon-Placeholder</label>
                                    <input value={cfg.form_phone_placeholder} onChange={(e) => upd({ form_phone_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>PLZ-Label</label>
                                    <input value={cfg.form_plz_label} onChange={(e) => upd({ form_plz_label: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>PLZ-Placeholder</label>
                                    <input value={cfg.form_plz_placeholder} onChange={(e) => upd({ form_plz_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Datenschutz-Text (mit &#123;link&#125;-Platzhalter)</label>
                                <input value={cfg.form_privacy_text} onChange={(e) => upd({ form_privacy_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-privacy-text" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <div>
                                    <label className={labelCls}>Datenschutz-Link-Text</label>
                                    <input value={cfg.form_privacy_link_text} onChange={(e) => upd({ form_privacy_link_text: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                                <div>
                                    <label className={labelCls}>Datenschutz-Link-URL</label>
                                    <input value={cfg.form_privacy_link_url} onChange={(e) => upd({ form_privacy_link_url: e.target.value })} className={inputCls} style={borderStyle} />
                                </div>
                            </div>
                            <div>
                                <label className={labelCls}>Erfolgs-Headline</label>
                                <input value={cfg.form_success_headline} onChange={(e) => upd({ form_success_headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-success-headline" />
                            </div>
                            <div>
                                <label className={labelCls}>Erfolgs-Body</label>
                                <textarea value={cfg.form_success_body} onChange={(e) => upd({ form_success_body: e.target.value })} rows={3} className={inputCls} style={borderStyle} data-testid="aktion-cfg-gp-success-body" />
                            </div>
                        </div>
                    </div>
                </details>
            </div>
        </div>
    );
}


/**
 * Editor-Block für das Welcome-Choice-Popup (Willkommens-Popup mit 2 Wegen).
 * Kompaktes Layout mit Master-Toggle, Trigger-Einstellungen und allen
 * sichtbaren Texten. Änderungen laufen über den normalen Save-Button oben.
 */
function WelcomeChoicePopupEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: WelcomeChoicePopupConfig;
    onChange: (next: WelcomeChoicePopupConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<WelcomeChoicePopupConfig>) => onChange({ ...cfg, ...patch });
    return (
        <div
            className="grid gap-4 mb-6 p-5 rounded-sm"
            style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}
            data-testid="aktion-cfg-welcome-popup"
        >
            <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                    Willkommens-Popup (Choice · 2 Wege)
                </div>
                <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                    <input
                        type="checkbox"
                        checked={!!cfg.enabled}
                        onChange={(e) => upd({ enabled: e.target.checked })}
                        className="w-4 h-4"
                        data-testid="aktion-cfg-wp-enabled"
                    />
                    <span className="font-medium">{cfg.enabled ? "Popup aktiv" : "Popup ausgeblendet"}</span>
                </label>
            </div>

            <div className={cfg.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                {/* Trigger */}
                <div className="grid gap-3 md:grid-cols-2 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div>
                        <label className={labelCls}>Delay bis Popup öffnet (ms)</label>
                        <input
                            type="number" min={0} max={30000}
                            value={cfg.trigger_delay_ms}
                            onChange={(e) => upd({ trigger_delay_ms: Math.max(0, parseInt(e.target.value) || 0) })}
                            className={inputCls} style={borderStyle}
                            data-testid="aktion-cfg-wp-delay"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">Empfohlen: 500 ms (nach Seiten-Load, damit DOM ready ist).</p>
                    </div>
                    <div>
                        <label className={labelCls}>Anzeige-Häufigkeit</label>
                        <div
                            className="w-full px-3 py-2 rounded-sm text-sm"
                            style={{ background: "hsl(var(--hf-bg))", border: "1.5px solid hsl(var(--hf-line))", color: "hsl(var(--hf-muted))" }}
                            data-testid="aktion-cfg-wp-throttle-info"
                        >
                            Bei jedem Aufruf von <code>/aktion</code>
                        </div>
                        <p className="text-[10px] text-hf-muted mt-1">Popup erscheint jetzt bei jedem Route-Aufruf und jedem Reload. Innerhalb derselben Sitzung (nach Schließen) nicht erneut.</p>
                    </div>
                </div>

                {/* Header-Texte */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Header-Texte</div>
                    <div>
                        <label className={labelCls}>Headline</label>
                        <input value={cfg.headline} onChange={(e) => upd({ headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-headline" />
                    </div>
                    <div>
                        <label className={labelCls}>Subline (Gold, klein)</label>
                        <input value={cfg.subline} onChange={(e) => upd({ subline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-subline" />
                    </div>
                </div>

                {/* Karte 1: Referenz */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                        Karte 1 · Referenz (100 %, Gold)
                    </div>
                    <div>
                        <label className={labelCls}>Titel</label>
                        <input value={cfg.card_referenz_title} onChange={(e) => upd({ card_referenz_title: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-ref-title" />
                    </div>
                    <div>
                        <label className={labelCls}>Body-Text</label>
                        <textarea value={cfg.card_referenz_body} onChange={(e) => upd({ card_referenz_body: e.target.value })} rows={3} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-ref-body" />
                    </div>
                    <div>
                        <label className={labelCls}>CTA-Button-Text</label>
                        <input value={cfg.card_referenz_cta} onChange={(e) => upd({ card_referenz_cta: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-ref-cta" />
                    </div>
                </div>

                {/* Karte 2: Rabatt */}
                <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">
                        Karte 2 · Rabatt (80 %, Rot)
                    </div>
                    <div>
                        <label className={labelCls}>Titel</label>
                        <input value={cfg.card_rabatt_title} onChange={(e) => upd({ card_rabatt_title: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-rab-title" />
                    </div>
                    <div>
                        <label className={labelCls}>Body-Text</label>
                        <textarea value={cfg.card_rabatt_body} onChange={(e) => upd({ card_rabatt_body: e.target.value })} rows={3} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-rab-body" />
                    </div>
                    <div>
                        <label className={labelCls}>CTA-Button-Text</label>
                        <input value={cfg.card_rabatt_cta} onChange={(e) => upd({ card_rabatt_cta: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-rab-cta" />
                    </div>
                </div>

                {/* Footer-Texte */}
                <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                    <div>
                        <label className={labelCls}>Helper-Text (unter den Karten)</label>
                        <input value={cfg.helper_text} onChange={(e) => upd({ helper_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-helper" />
                    </div>
                    <div>
                        <label className={labelCls}>Dismiss-Text (grauer Text-Link)</label>
                        <input value={cfg.dismiss_text} onChange={(e) => upd({ dismiss_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-wp-dismiss" />
                    </div>
                </div>
            </div>
        </div>
    );
}


/**
 * Editor-Block für das Referenz-Popup (mode="referenz" von `GutscheinPopup`).
 * Kapselt alle Texte, Trust-Chips, Formular-Labels und Erfolgs-Meldungen des
 * neuen Layout mit Gold-Zertifikats-Siegel. Wird separat vom Rabatt-Popup
 * gespeichert und getrackt (Kampagne `referenz-gutschein`).
 */
function ReferenzPopupEditor({
    cfg, onChange, inputCls, labelCls, borderStyle,
}: {
    cfg: ReferenzPopupConfig;
    onChange: (next: ReferenzPopupConfig) => void;
    inputCls: string;
    labelCls: string;
    borderStyle: React.CSSProperties;
}) {
    const upd = (patch: Partial<ReferenzPopupConfig>) => onChange({ ...cfg, ...patch });
    const badges = cfg.trust_badges || [];
    const setBadge = (i: number, v: string) => {
        const copy = [...badges];
        copy[i] = v;
        upd({ trust_badges: copy });
    };
    const addBadge = () => upd({ trust_badges: [...badges, ""] });
    const removeBadge = (i: number) => upd({ trust_badges: badges.filter((_, idx) => idx !== i) });

    return (
        <div
            className="grid gap-4 mb-6 p-5 rounded-sm"
            style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}
            data-testid="aktion-cfg-referenz-popup"
        >
            <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                Referenz-Popup · Texte (Kampagne referenz-gutschein)
            </div>

            {/* Kopf-Texte */}
            <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Kopf-Texte</div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Gold-Eyebrow (klein oben)</label>
                        <input value={cfg.kicker} onChange={(e) => upd({ kicker: e.target.value })} className={inputCls} style={borderStyle} placeholder="HEUTE" data-testid="aktion-cfg-rp-kicker" />
                    </div>
                    <div>
                        <label className={labelCls}>Headline (Anton, riesig)</label>
                        <input value={cfg.headline} onChange={(e) => upd({ headline: e.target.value })} className={inputCls} style={borderStyle} placeholder="GROSSE REFERENZ-AKTION" data-testid="aktion-cfg-rp-headline" />
                    </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Zertifikats-Siegel · Text oben</label>
                        <input value={cfg.seal_top} onChange={(e) => upd({ seal_top: e.target.value })} className={inputCls} style={borderStyle} placeholder="100 %" data-testid="aktion-cfg-rp-seal-top" />
                    </div>
                    <div>
                        <label className={labelCls}>Zertifikats-Siegel · Text unten</label>
                        <input value={cfg.seal_bottom} onChange={(e) => upd({ seal_bottom: e.target.value })} className={inputCls} style={borderStyle} placeholder="KOSTENLOS" data-testid="aktion-cfg-rp-seal-bottom" />
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Italic Serif-Tagline (unter Siegel)</label>
                    <input value={cfg.tagline} onChange={(e) => upd({ tagline: e.target.value })} className={inputCls} style={borderStyle} placeholder="Ihr Haus als HypeFly-Referenz" data-testid="aktion-cfg-rp-tagline" />
                </div>
            </div>

            {/* Trust-Chips */}
            <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                <div className="flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Trust-Chips (max. 4, 2 pro Zeile)</div>
                    <button type="button" onClick={addBadge} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs" style={{ background: "#F8FAFC", ...borderStyle }} data-testid="aktion-cfg-rp-badge-add" disabled={badges.length >= 4}>
                        <Plus size={14} /> Chip hinzufügen
                    </button>
                </div>
                {badges.map((b, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <input
                            value={b}
                            onChange={(e) => setBadge(i, e.target.value)}
                            className={inputCls + " flex-1"} style={borderStyle}
                            placeholder="UNVERBINDLICH RESERVIEREN"
                            data-testid={`aktion-cfg-rp-badge-${i}`}
                        />
                        <button type="button" onClick={() => removeBadge(i)} className="p-1.5 rounded-sm" style={{ color: "#B91C1C" }} aria-label="Entfernen">
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
                {badges.length === 0 && (
                    <p className="text-xs text-hf-muted italic">Noch keine Chips — mit &bdquo;+&ldquo; hinzuf&uuml;gen.</p>
                )}
            </div>

            {/* Formular-Labels */}
            <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Formular</div>
                <div>
                    <label className={labelCls}>Formular-Headline (über den Feldern)</label>
                    <input value={cfg.form_headline} onChange={(e) => upd({ form_headline: e.target.value })} className={inputCls} style={borderStyle} placeholder="JETZT REFERENZPLATZ SICHERN" data-testid="aktion-cfg-rp-form-headline" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Name-Label</label>
                        <input value={cfg.form_name_label} onChange={(e) => upd({ form_name_label: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Name-Placeholder</label>
                        <input value={cfg.form_name_placeholder} onChange={(e) => upd({ form_name_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Telefon-Label</label>
                        <input value={cfg.form_phone_label} onChange={(e) => upd({ form_phone_label: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Telefon-Placeholder</label>
                        <input value={cfg.form_phone_placeholder} onChange={(e) => upd({ form_phone_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>PLZ-Label</label>
                        <input value={cfg.form_plz_label} onChange={(e) => upd({ form_plz_label: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>PLZ-Placeholder</label>
                        <input value={cfg.form_plz_placeholder} onChange={(e) => upd({ form_plz_placeholder: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                </div>
                <div>
                    <label className={labelCls}>Datenschutz-Text (mit &#123;link&#125;-Platzhalter)</label>
                    <input value={cfg.form_privacy_text} onChange={(e) => upd({ form_privacy_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-rp-privacy-text" />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                    <div>
                        <label className={labelCls}>Datenschutz-Link-Text</label>
                        <input value={cfg.form_privacy_link_text} onChange={(e) => upd({ form_privacy_link_text: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Datenschutz-Link-URL</label>
                        <input value={cfg.form_privacy_link_url} onChange={(e) => upd({ form_privacy_link_url: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                </div>
            </div>

            {/* CTA + Footer */}
            <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                <div>
                    <label className={labelCls}>Gold-CTA-Button-Text</label>
                    <input value={cfg.cta_text} onChange={(e) => upd({ cta_text: e.target.value })} className={inputCls} style={borderStyle} placeholder="REFERENZPLATZ UNVERBINDLICH SICHERN" data-testid="aktion-cfg-rp-cta" />
                </div>
                <div>
                    <label className={labelCls}>Footer-Zeile (unten, klein)</label>
                    <input value={cfg.footer_text} onChange={(e) => upd({ footer_text: e.target.value })} className={inputCls} style={borderStyle} placeholder="Sie sichern sich nur den Platz — kein Kauf, keine Verpflichtung." data-testid="aktion-cfg-rp-footer" />
                </div>
            </div>

            {/* Erfolgs-Overlay */}
            <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Nach erfolgreichem Absenden</div>
                <div>
                    <label className={labelCls}>Erfolgs-Headline</label>
                    <input value={cfg.form_success_headline} onChange={(e) => upd({ form_success_headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-rp-success-headline" />
                </div>
                <div>
                    <label className={labelCls}>Erfolgs-Body</label>
                    <textarea value={cfg.form_success_body} onChange={(e) => upd({ form_success_body: e.target.value })} rows={3} className={inputCls} style={borderStyle} data-testid="aktion-cfg-rp-success-body" />
                </div>
            </div>
        </div>
    );
}



type SaveStatus = "idle" | "saving" | "saved" | "error";

export default function AdminAktionsSeite() {
    const [mode, setMode] = useState<"editorial" | "aktion">("editorial");
    const [cfg, setCfg] = useState<AktionPageConfig>(DEFAULT_AKTION_PAGE_CONFIG);
    const [loading, setLoading] = useState(true);
    const [modeStatus, setModeStatus] = useState<SaveStatus>("idle");
    const [cfgStatus, setCfgStatus] = useState<SaveStatus>("idle");
    const [error, setError] = useState<string | null>(null);
    const [mediaLibrary, setMediaLibrary] = useState<MediaDoc[]>([]);

    // Load von Backend beim Mount.
    useEffect(() => {
        (async () => {
            const res = await api<{ items: Record<string, Record<string, unknown>> }>(
                "/api/admin/site-content",
            );
            if (res.ok && res.data && res.data.items) {
                const hm = res.data.items["homepage_mode"];
                if (hm && typeof hm === "object" && typeof hm.mode === "string") {
                    setMode(hm.mode === "aktion" ? "aktion" : "editorial");
                }
                const ap = res.data.items["aktion_page"];
                if (ap && typeof ap === "object") {
                    setCfg((prev) => ({ ...prev, ...(ap as Partial<AktionPageConfig>) }));
                }
            }
            setLoading(false);
        })();
    }, []);

    // Mediathek laden (für den Media-Picker in den Vergleichs-Slots).
    useEffect(() => {
        (async () => {
            const res = await api<{ items: MediaDoc[] }>("/api/admin/media");
            if (res.ok && res.data && Array.isArray(res.data.items)) {
                setMediaLibrary(res.data.items);
            }
        })();
    }, []);

    const saveMode = useCallback(async (next: "editorial" | "aktion") => {
        // Optimistic UI: sofort visuell umschalten, damit der Toggle
        // responsive wirkt. Bei API-Fehler rollen wir unten wieder zurück.
        const previous = mode;
        setMode(next);
        setModeStatus("saving");
        setError(null);
        const res = await api("/api/admin/site-content/homepage_mode", {
            method: "PUT",
            // WICHTIG: FastAPI verlangt Content-Type: application/json,
            // sonst antwortet der Endpoint mit 422 „Input should be a valid dictionary".
            // Der zentrale api()-Helper setzt den Header nicht automatisch.
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: { mode: next } }),
        });
        if (res.ok) {
            setModeStatus("saved");
            toast.success(
                next === "aktion"
                    ? "Startseite zeigt jetzt die Aktions-Landing."
                    : "Startseite zeigt jetzt die Editorial-Version.",
                { duration: 2500 },
            );
            setTimeout(() => setModeStatus("idle"), 2000);
        } else {
            // Rollback des optimistischen Wechsels.
            setMode(previous);
            setModeStatus("error");
            setError(res.error || "Modus konnte nicht gespeichert werden.");
            toast.error(res.error || "Modus konnte nicht gespeichert werden.");
        }
    }, [mode]);

    const saveCfg = useCallback(async () => {
        setCfgStatus("saving");
        setError(null);
        const res = await api("/api/admin/site-content/aktion_page", {
            method: "PUT",
            // Selber JSON-Header-Fix wie oben.
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: cfg }),
        });
        if (res.ok) {
            setCfgStatus("saved");
            toast.success("Inhalte der Aktions-Seite gespeichert.", { duration: 2500 });
            setTimeout(() => setCfgStatus("idle"), 2000);
        } else {
            setCfgStatus("error");
            setError(res.error || "Konfiguration konnte nicht gespeichert werden.");
            toast.error(res.error || "Konfiguration konnte nicht gespeichert werden.");
        }
    }, [cfg]);

    /**
     * Master-Switch der `/aktion`-Landing sofort persistieren (analog zum
     * Betriebsmodus-Toggle oben). Wir schreiben das komplette `aktion_page`-
     * Objekt mit gepatchtem `enabled`-Feld — der Rest bleibt erhalten.
     * Optimistic UI mit Rollback bei API-Fehler.
     */
    const savePageEnabled = useCallback(async (next: boolean) => {
        const previous = cfg.enabled !== false;
        const nextCfg = { ...cfg, enabled: next };
        setCfg(nextCfg);
        const res = await api("/api/admin/site-content/aktion_page", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ value: nextCfg }),
        });
        if (res.ok) {
            toast.success(
                next
                    ? "Aktions-Landing aktiviert — /aktion + Header-Button sichtbar."
                    : "Aktions-Landing ausgeschaltet — /aktion redirected + Header-Button verschwindet.",
                { duration: 3000 },
            );
        } else {
            // Rollback auf vorherigen Zustand.
            setCfg((prev) => ({ ...prev, enabled: previous }));
            toast.error(res.error || "Master-Switch konnte nicht gespeichert werden.");
        }
    }, [cfg]);

    if (loading) {
        return (
            <div className="max-w-4xl mx-auto py-10 px-4">
                <p className="text-hf-muted text-sm">Wird geladen…</p>
            </div>
        );
    }

    const inputCls = "w-full px-3 py-2 rounded-sm bg-white text-sm";
    const labelCls = "block text-xs uppercase tracking-wider mb-1";
    const borderStyle = { border: "1px solid hsl(var(--hf-line))" };

    return (
        <div className="max-w-4xl mx-auto py-8 px-4">
            <Helmet>
                <title>Aktions-Seite · HypeFly Admin</title>
            </Helmet>

            <header className="mb-8 flex items-start gap-3">
                <Sparkles size={28} strokeWidth={1.5} className="text-hf-ink mt-1" />
                <div className="flex-1">
                    <h1 className="font-display text-2xl md:text-3xl text-hf-ink">Aktions-Seite</h1>
                    <p className="text-hf-muted text-sm mt-1">
                        Landingpage für Werbeanzeigen-Traffic (TikTok, Meta). Modus und Inhalte hier bearbeiten.
                    </p>
                </div>
                <a
                    href="/aktion"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm underline"
                    style={{ color: "#8A5A1D" }}
                    data-testid="aktion-preview-link"
                >
                    Vorschau öffnen <ExternalLink size={12} />
                </a>
            </header>

            {/* ═══ VIDEO-HERO EDITOR ═══
             *
             * Ganz oben, damit der Betreiber sofort sieht, dass dieses
             * prominente Element existiert. Kann komplett deaktiviert
             * werden → bestehendes Layout unverändert.
             */}
            <VideoHeroEditor
                cfg={cfg.video_hero}
                onChange={(next) => setCfg({ ...cfg, video_hero: next })}
                inputCls={inputCls}
                labelCls={labelCls}
                borderStyle={borderStyle}
            />

            {/* ═══ HERO-CHOICE-TILES EDITOR ═══
             *
             * Permanente Auswahl-Kacheln neben dem Hero-CTA (ersetzt seit
             * dem Redesign das auto-öffnende Welcome-Popup).
             */}
            <HeroChoiceTilesEditor
                cfg={cfg.hero_choice_tiles}
                onChange={(next) => setCfg({ ...cfg, hero_choice_tiles: next })}
                inputCls={inputCls}
                labelCls={labelCls}
                borderStyle={borderStyle}
            />

            {/* ═══ HERO-VIDEO-TILES EDITOR ═══
             *
             * Zwei Video-Teaser-Kacheln neben den Choice-Kacheln. Klick öffnet
             * Fullscreen-Modal mit Ton. Vorschau in der Kachel läuft stumm im
             * Loop. Master-Toggle deaktiviert beide Kacheln zusammen. */}
            <HeroVideoTilesEditor
                cfg={cfg.hero_video_tiles}
                onChange={(next) => setCfg({ ...cfg, hero_video_tiles: next })}
                inputCls={inputCls}
                labelCls={labelCls}
                borderStyle={borderStyle}
            />

            {/* ═══ SEKTION 0 · MASTER-SWITCH LANDING ═══
             *
             * Steuert den CMS-Key `aktion_page.enabled` (Boolean). Bei AUS:
             *  - Route `/aktion` redirected via <AktionGate/> nach `/`.
             *  - Header-AKTION-Button verschwindet auf allen Seiten.
             *
             * WICHTIG: nicht verwechseln mit dem `aktion.enabled`-Feld unter
             * /admin/aktion — das steuert die Referenz-Sektion + den Banner
             * auf der Startseite. Die beiden Schalter sind bewusst entkoppelt.
             */}
            <section
                className="mb-6 p-6 rounded-md"
                style={{
                    background: cfg.enabled !== false
                        ? "linear-gradient(180deg, hsl(var(--hf-surface)) 0%, hsl(var(--hf-bg)) 100%)"
                        : "hsl(var(--hf-danger) / 0.06)",
                    border: `1.5px solid ${cfg.enabled !== false ? "hsl(var(--hf-gold))" : "hsl(var(--hf-danger) / 0.4)"}`,
                    boxShadow: "var(--shadow-warm-sm)",
                }}
                data-testid="aktion-page-master-switch-card"
            >
                <div className="flex items-start md:items-center justify-between gap-4 flex-wrap md:flex-nowrap">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">
                            Sektion 0 · Sichtbarkeit
                        </div>
                        <h2 className="font-display text-xl md:text-2xl text-hf-ink leading-tight mt-0.5">
                            Aktions-Landing {cfg.enabled !== false ? "aktiv" : "ausgeschaltet"}
                        </h2>
                        <p className="text-xs md:text-sm mt-2 max-w-2xl text-hf-muted">
                            Master-Switch für die separate Route <code>/aktion</code> und den{" "}
                            <strong>AKTION-Button</strong> im Header. Bei <strong>AUS</strong> leitet{" "}
                            <code>/aktion</code> automatisch zur Startseite um und der Header-Button
                            verschwindet auf allen Seiten. <em>Unabhängig</em> vom{" "}
                            <code>aktion.enabled</code>-Feld unter <code>/admin/aktion</code>{" "}
                            (das steuert die Referenz-Sektion + Banner auf der Startseite).
                        </p>
                    </div>
                    <label
                        className="inline-flex items-center gap-3 cursor-pointer shrink-0 select-none"
                        data-testid="aktion-page-master-toggle-label"
                    >
                        <span
                            className="text-xs uppercase tracking-[0.14em]"
                            style={{
                                color: cfg.enabled !== false ? "hsl(var(--hf-gold))" : "hsl(var(--hf-danger))",
                                fontWeight: 700,
                            }}
                        >
                            {cfg.enabled !== false ? "AN" : "AUS"}
                        </span>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={cfg.enabled !== false}
                            onClick={() => savePageEnabled(!(cfg.enabled !== false))}
                            data-testid="aktion-page-master-toggle"
                            className="relative inline-block w-14 h-8 rounded-full transition-colors focus:outline-none focus-visible:ring-2"
                            style={{
                                background: cfg.enabled !== false ? "hsl(var(--hf-gold))" : "hsl(var(--hf-line))",
                            }}
                        >
                            <span
                                className="absolute top-1 left-1 w-6 h-6 rounded-full transition-transform"
                                style={{
                                    background: "#FFFDF9",
                                    transform: cfg.enabled !== false ? "translateX(24px)" : "translateX(0)",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                                }}
                            />
                        </button>
                    </label>
                </div>
            </section>

            {/* Header-Button-Beschriftung — dynamisiert den globalen AKTION-CTA
             *  im Header (Chip + Haupttext + optionales Flame-Icon). */}
            <section
                className="mb-6 p-5 rounded-md"
                style={{
                    background: "hsl(var(--hf-surface))",
                    border: "1.5px solid hsl(var(--hf-line))",
                }}
                data-testid="aktion-cfg-header-button"
            >
                <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-3">
                    Header-Button · Beschriftung
                </div>
                <div className="grid gap-3 md:grid-cols-3 p-3 rounded-sm bg-white" style={{ border: "1.5px solid hsl(var(--hf-line))" }}>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] block mb-1 text-hf-muted">Chip-Text</label>
                        <input
                            value={cfg.header_button.chip_text}
                            onChange={(e) => setCfg({ ...cfg, header_button: { ...cfg.header_button, chip_text: e.target.value } })}
                            className="w-full px-3 py-2 rounded-sm text-sm bg-white"
                            style={{ border: "1.5px solid hsl(var(--hf-line))" }}
                            data-testid="aktion-cfg-hb-chip"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">Kleiner Gold-Chip, z.&nbsp;B. &bdquo;Aktion&ldquo;.</p>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.18em] block mb-1 text-hf-muted">Haupttext</label>
                        <input
                            value={cfg.header_button.main_text}
                            onChange={(e) => setCfg({ ...cfg, header_button: { ...cfg.header_button, main_text: e.target.value } })}
                            className="w-full px-3 py-2 rounded-sm text-sm bg-white"
                            style={{ border: "1.5px solid hsl(var(--hf-line))" }}
                            data-testid="aktion-cfg-hb-main"
                        />
                        <p className="text-[10px] text-hf-muted mt-1">z. B. &bdquo;100 H&auml;user gesucht&ldquo; (nur auf xl+ sichtbar).</p>
                    </div>
                    <div className="flex items-end">
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="aktion-cfg-hb-icon-label">
                            <input
                                type="checkbox"
                                checked={cfg.header_button.show_icon}
                                onChange={(e) => setCfg({ ...cfg, header_button: { ...cfg.header_button, show_icon: e.target.checked } })}
                                className="w-4 h-4"
                                data-testid="aktion-cfg-hb-icon"
                            />
                            <span>Flame-Icon im Chip anzeigen 🔥</span>
                        </label>
                    </div>
                </div>
                <p className="text-[11px] text-hf-muted mt-3">
                    Änderungen greifen auf der gesamten Website (globaler Header) sofort nach dem Speichern.
                </p>
            </section>

            {/* ═══ BETRIEBSMODUS ═══ */}
            <section
                className="mb-10 p-6 rounded-sm"
                style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}
                data-testid="admin-homepage-mode-section"
            >
                <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-2">
                    Betriebsmodus der Startseite
                </div>
                <h2 className="font-display text-xl text-hf-ink mb-3">
                    Was zeigt <code className="text-sm bg-white px-1.5 py-0.5 rounded-sm">hypefly.de/</code> aktuell?
                </h2>
                <p className="text-hf-muted text-sm mb-5">
                    <strong>Editorial</strong> = die elegante Standard-Seite.{" "}
                    <strong>Aktion</strong> = die Werbeanzeigen-Landing (identisch zu <code>/aktion</code>).
                    Änderung wirkt sofort — kein Deploy nötig.
                </p>
                <div className="flex flex-wrap gap-3">
                    {(["editorial", "aktion"] as const).map((m) => (
                        <button
                            key={m}
                            type="button"
                            onClick={() => saveMode(m)}
                            className="px-5 py-3 rounded-sm text-sm font-medium transition-all"
                            style={{
                                background: mode === m ? "#C48B34" : "white",
                                color: mode === m ? "white" : "hsl(var(--hf-ink))",
                                border: `2px solid ${mode === m ? "#8A5A1D" : "hsl(var(--hf-line))"}`,
                                boxShadow: mode === m ? "0 2px 8px -2px rgba(196, 139, 52, 0.4)" : "none",
                            }}
                            data-testid={`admin-mode-btn-${m}`}
                        >
                            {m === "editorial" ? "Editorial (Standard)" : "Aktion (Werbe-Landing)"}
                        </button>
                    ))}
                    {modeStatus === "saving" && <span className="text-sm text-hf-muted self-center">Wird gespeichert…</span>}
                    {modeStatus === "saved" && <span className="text-sm text-green-700 self-center">✓ Gespeichert</span>}
                </div>
                <p className="text-xs text-hf-muted mt-3">
                    Aktueller Modus: <strong>{mode === "aktion" ? "Aktion" : "Editorial"}</strong>. Die Route{" "}
                    <code>/aktion</code> bleibt unabhängig davon immer erreichbar.
                </p>
            </section>

            {/* ═══ CONTENT-EDITOR ═══ */}
            <section data-testid="admin-aktion-page-config">
                <h2 className="font-display text-xl text-hf-ink mb-4">Inhalte der Aktions-Seite</h2>

                {/* Hero */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Hero</div>
                    <div>
                        <label className={labelCls}>Headline (bold uppercase)</label>
                        <input value={cfg.hero_headline} onChange={(e) => setCfg({ ...cfg, hero_headline: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-hero-headline" />
                    </div>
                    <div>
                        <label className={labelCls}>Brushstroke-Text (roter Banner)</label>
                        <input value={cfg.hero_brushstroke} onChange={(e) => setCfg({ ...cfg, hero_brushstroke: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-hero-brush" />
                    </div>
                    <div>
                        <label className={labelCls}>Subline (der Kontext-Satz)</label>
                        <textarea value={cfg.hero_subline} onChange={(e) => setCfg({ ...cfg, hero_subline: e.target.value })} rows={2} className={inputCls} style={borderStyle} data-testid="aktion-cfg-hero-subline" />
                    </div>
                    <div>
                        <label className={labelCls}>Siegel-Text (nutze &bdquo;&middot;&ldquo; als Trennzeichen zwischen Zeilen)</label>
                        <input value={cfg.seal_text} onChange={(e) => setCfg({ ...cfg, seal_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-seal" />
                    </div>
                </div>

                {/* Counter */}
                <div className="grid gap-4 md:grid-cols-2 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="md:col-span-2 text-[10px] uppercase tracking-[0.22em] text-hf-muted">Zähler</div>
                    <div>
                        <label className={labelCls}>Gesamt-Plätze</label>
                        <input type="number" min={1} value={cfg.counter_total} onChange={(e) => setCfg({ ...cfg, counter_total: Math.max(1, parseInt(e.target.value) || 1) })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-total" />
                    </div>
                    <div>
                        <label className={labelCls}>Bereits belegt</label>
                        <input type="number" min={0} value={cfg.counter_slots_taken} onChange={(e) => setCfg({ ...cfg, counter_slots_taken: Math.max(0, parseInt(e.target.value) || 0) })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-taken" />
                    </div>
                    <div className="md:col-span-2 text-sm text-hf-muted">
                        Angezeigt wird: <strong>{Math.max(0, cfg.counter_total - cfg.counter_slots_taken)} von {cfg.counter_total} noch frei</strong>
                    </div>
                </div>

                {/* CTAs */}
                <div className="grid gap-4 md:grid-cols-2 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="md:col-span-2 text-[10px] uppercase tracking-[0.22em] text-hf-muted">CTA-Buttons</div>
                    <div>
                        <label className={labelCls}>Primär (Hero)</label>
                        <input value={cfg.cta_primary_text} onChange={(e) => setCfg({ ...cfg, cta_primary_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cta-primary" />
                    </div>
                    <div>
                        <label className={labelCls}>Final (Formular)</label>
                        <input value={cfg.cta_final_text} onChange={(e) => setCfg({ ...cfg, cta_final_text: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cta-final" />
                    </div>
                </div>

                {/* Features */}
                <div className="mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-3">4 Feature-Karten</div>
                    <div className="grid gap-4">
                        {cfg.features.map((f, i) => (
                            <div key={i} className="grid grid-cols-1 md:grid-cols-[100px_1fr_1fr] gap-3 items-start p-3 rounded-sm bg-white" style={borderStyle}>
                                <div>
                                    <label className={labelCls}>Icon</label>
                                    <select value={f.icon} onChange={(e) => {
                                        const copy = [...cfg.features];
                                        copy[i] = { ...f, icon: e.target.value };
                                        setCfg({ ...cfg, features: copy });
                                    }} className={inputCls} style={borderStyle} data-testid={`aktion-cfg-feature-${i}-icon`}>
                                        <option value="camera">Kamera</option>
                                        <option value="compass">Kompass</option>
                                        <option value="film">Film</option>
                                        <option value="home">Haus</option>
                                        <option value="cloud">Cloud</option>
                                        <option value="monitor">Monitor</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Titel</label>
                                    <input value={f.title} onChange={(e) => {
                                        const copy = [...cfg.features];
                                        copy[i] = { ...f, title: e.target.value };
                                        setCfg({ ...cfg, features: copy });
                                    }} className={inputCls} style={borderStyle} data-testid={`aktion-cfg-feature-${i}-title`} />
                                </div>
                                <div>
                                    <label className={labelCls}>Untertext</label>
                                    <input value={f.subtitle} onChange={(e) => {
                                        const copy = [...cfg.features];
                                        copy[i] = { ...f, subtitle: e.target.value };
                                        setCfg({ ...cfg, features: copy });
                                    }} className={inputCls} style={borderStyle} data-testid={`aktion-cfg-feature-${i}-subtitle`} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Warum Kostenlos */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Warum kostenlos?</div>
                    <div>
                        <label className={labelCls}>Überschrift</label>
                        <input value={cfg.warum_kostenlos_title} onChange={(e) => setCfg({ ...cfg, warum_kostenlos_title: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Text</label>
                        <textarea value={cfg.warum_kostenlos} onChange={(e) => setCfg({ ...cfg, warum_kostenlos: e.target.value })} rows={3} className={inputCls} style={borderStyle} data-testid="aktion-cfg-warum" />
                    </div>
                </div>

                {/* Demo */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">360°-Demo</div>
                    <div>
                        <label className={labelCls}>Überschrift</label>
                        <input value={cfg.demo_headline} onChange={(e) => setCfg({ ...cfg, demo_headline: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Hinweistext</label>
                        <input value={cfg.demo_hint} onChange={(e) => setCfg({ ...cfg, demo_hint: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                    <div>
                        <label className={labelCls}>Media-ID (aus /admin/medien)</label>
                        <input value={cfg.demo_media_id} onChange={(e) => setCfg({ ...cfg, demo_media_id: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-demo-media" />
                    </div>
                </div>

                {/* Vergleichs-Sektion (Damals vs. Heute) ─────────────────
                 * Text-Blöcke + Bildpfad für die Vergleichs-Sektion, die
                 * zwischen Features und Trust-Box läuft. Das 360°-Video
                 * wird oben in „360°-Demo" gepflegt und automatisch dort
                 * eingesetzt. */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid="aktion-cfg-comparison">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Vergleichs-Sektion (Damals vs. Heute)</div>
                    <div>
                        <label className={labelCls}>Kicker (klein, gold, uppercase)</label>
                        <input value={cfg.comparison_kicker} onChange={(e) => setCfg({ ...cfg, comparison_kicker: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-kicker" />
                    </div>
                    <div>
                        <label className={labelCls}>Sektions-Titel (Serif)</label>
                        <input value={cfg.comparison_title} onChange={(e) => setCfg({ ...cfg, comparison_title: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-title" />
                    </div>
                    <div>
                        <label className={labelCls}>Subline (Fließtext)</label>
                        <textarea value={cfg.comparison_subline} onChange={(e) => setCfg({ ...cfg, comparison_subline: e.target.value })} rows={4} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-subline" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelCls}>Label unter Vintage-Bild</label>
                            <input value={cfg.comparison_vintage_label} onChange={(e) => setCfg({ ...cfg, comparison_vintage_label: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-vintage-label" />
                        </div>
                        <div>
                            <label className={labelCls}>Label unter 360°-Video</label>
                            <input value={cfg.comparison_modern_label} onChange={(e) => setCfg({ ...cfg, comparison_modern_label: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-modern-label" />
                        </div>
                    </div>
                    <div>
                        <label className={labelCls}>Bildpfad Vintage-Bild (Legacy · nur Fallback)</label>
                        <input value={cfg.comparison_vintage_image || ""} onChange={(e) => setCfg({ ...cfg, comparison_vintage_image: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cmp-vintage-image" />
                        <p className="text-xs text-hf-muted mt-1">
                            Nur relevant für alte CMS-Configs. Aktive Kacheln werden unten in den beiden Slot-Editoren gepflegt.
                        </p>
                    </div>
                </div>

                {/* Vergleichs-Slots (universelle Media-Kacheln inkl. Slideshow) */}
                <div className="mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid="aktion-cfg-comparison-slots">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-3">
                        Vergleichs-Kacheln — Medien &amp; Slideshow
                    </div>
                    <p className="text-xs text-hf-muted mb-4">
                        Beide Kacheln akzeptieren Foto, Video, 360°-Foto und 360°-Video. Slideshow-Modus wechselt zyklisch zwischen den Medien.
                        Upload direkt in der Zeile möglich, oder aus der Mediathek picken. {mediaLibrary.length > 0 ? `${mediaLibrary.length} Medien in der Mediathek.` : "Mediathek wird geladen …"}
                    </p>
                    <div className="grid gap-4 md:grid-cols-2">
                        <ComparisonSlotEditor
                            label="Linke Kachel · Damals (Default: Vintage-Slideshow)"
                            slot={cfg.comparison_left}
                            onChange={(next) => setCfg({ ...cfg, comparison_left: next })}
                            testIdPrefix="aktion-cfg-cmp-left"
                            mediaLibrary={mediaLibrary}
                        />
                        <ComparisonSlotEditor
                            label="Rechte Kachel · Heute (Default: 360°-Video)"
                            slot={cfg.comparison_right}
                            onChange={(next) => setCfg({ ...cfg, comparison_right: next })}
                            testIdPrefix="aktion-cfg-cmp-right"
                            mediaLibrary={mediaLibrary}
                        />
                    </div>
                </div>

                {/* Steps */}
                <div className="mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted mb-3">4 Prozess-Schritte</div>
                    <div className="grid gap-4">
                        {cfg.steps.map((s, i) => (
                            <div key={i} className="grid grid-cols-1 md:grid-cols-[100px_1fr_1fr] gap-3 items-start p-3 rounded-sm bg-white" style={borderStyle}>
                                <div>
                                    <label className={labelCls}>Icon</label>
                                    <select value={s.icon} onChange={(e) => {
                                        const copy = [...cfg.steps];
                                        copy[i] = { ...s, icon: e.target.value };
                                        setCfg({ ...cfg, steps: copy });
                                    }} className={inputCls} style={borderStyle}>
                                        <option value="home">Haus</option>
                                        <option value="camera">Kamera</option>
                                        <option value="cloud">Cloud</option>
                                        <option value="monitor">Monitor</option>
                                        <option value="compass">Kompass</option>
                                        <option value="film">Film</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>Titel (Schritt {i + 1})</label>
                                    <input value={s.title} onChange={(e) => {
                                        const copy = [...cfg.steps];
                                        copy[i] = { ...s, title: e.target.value };
                                        setCfg({ ...cfg, steps: copy });
                                    }} className={inputCls} style={borderStyle} data-testid={`aktion-cfg-step-${i}-title`} />
                                </div>
                                <div>
                                    <label className={labelCls}>Untertext</label>
                                    <input value={s.subtitle} onChange={(e) => {
                                        const copy = [...cfg.steps];
                                        copy[i] = { ...s, subtitle: e.target.value };
                                        setCfg({ ...cfg, steps: copy });
                                    }} className={inputCls} style={borderStyle} />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Special Offer (Aktions-Angebot) — ersetzt die alte Urgency-Sektion.
                 * Komplett eigenständig: eigener Zähler, eigene Texte, eigene
                 * Typografie, eigener CTA (Form-Scroll oder externe URL) und
                 * Ein/Aus-Toggle. Das alte Feld `urgency_headline` bleibt in
                 * der Config als Legacy, wird aber nicht mehr gerendert. */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid="aktion-cfg-special-offer">
                    <div className="flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Aktions-Angebot (eigenständige Sektion)</div>
                        <label className="inline-flex items-center gap-2 text-sm cursor-pointer" data-testid="aktion-cfg-special-offer-toggle-wrap">
                            <input
                                type="checkbox"
                                checked={!!cfg.special_offer.enabled}
                                onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, enabled: e.target.checked } })}
                                className="w-4 h-4"
                                data-testid="aktion-cfg-special-offer-enabled"
                            />
                            <span className="font-medium">{cfg.special_offer.enabled ? "Sektion sichtbar" : "Sektion ausgeblendet"}</span>
                        </label>
                    </div>

                    <div className={cfg.special_offer.enabled ? "" : "opacity-40 pointer-events-none select-none"}>
                        {/* Zähler-Sub-Block */}
                        <div className="grid gap-3 md:grid-cols-4 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                            <label className="inline-flex items-center gap-2 text-sm">
                                <input
                                    type="checkbox"
                                    checked={!!cfg.special_offer.counter_enabled}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, counter_enabled: e.target.checked } })}
                                    data-testid="aktion-cfg-so-counter-enabled"
                                />
                                Zähler anzeigen
                            </label>
                            <div>
                                <label className={labelCls}>Gesamt-Plätze</label>
                                <input
                                    type="number" min={1}
                                    value={cfg.special_offer.counter_total}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, counter_total: Math.max(1, parseInt(e.target.value) || 1) } })}
                                    className={inputCls} style={borderStyle}
                                    data-testid="aktion-cfg-so-counter-total"
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Bereits belegt</label>
                                <input
                                    type="number" min={0}
                                    value={cfg.special_offer.counter_taken}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, counter_taken: Math.max(0, parseInt(e.target.value) || 0) } })}
                                    className={inputCls} style={borderStyle}
                                    data-testid="aktion-cfg-so-counter-taken"
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Label</label>
                                <input
                                    value={cfg.special_offer.counter_label}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, counter_label: e.target.value } })}
                                    className={inputCls} style={borderStyle}
                                    placeholder="noch frei"
                                    data-testid="aktion-cfg-so-counter-label"
                                />
                            </div>
                            <div className="md:col-span-4 text-xs text-hf-muted">
                                Vorschau: <strong>{Math.max(0, cfg.special_offer.counter_total - cfg.special_offer.counter_taken)}</strong> von {cfg.special_offer.counter_total} {cfg.special_offer.counter_label || "noch frei"}
                            </div>
                        </div>

                        {/* Text-Sub-Block */}
                        <div className="grid gap-3 p-3 rounded-sm bg-white mb-3" style={borderStyle}>
                            <div>
                                <label className={labelCls}>Überschrift</label>
                                <input
                                    value={cfg.special_offer.headline}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, headline: e.target.value } })}
                                    className={inputCls} style={borderStyle}
                                    data-testid="aktion-cfg-so-headline"
                                />
                            </div>
                            <div>
                                <label className={labelCls}>Untertitel (optional)</label>
                                <input
                                    value={cfg.special_offer.subline}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, subline: e.target.value } })}
                                    className={inputCls} style={borderStyle}
                                    data-testid="aktion-cfg-so-subline"
                                />
                            </div>
                            <div className="grid md:grid-cols-[1fr_180px_1fr] gap-3">
                                <div>
                                    <label className={labelCls}>CTA-Button-Text</label>
                                    <input
                                        value={cfg.special_offer.cta_text}
                                        onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, cta_text: e.target.value } })}
                                        className={inputCls} style={borderStyle}
                                        data-testid="aktion-cfg-so-cta-text"
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>CTA-Ziel</label>
                                    <select
                                        value={cfg.special_offer.cta_target}
                                        onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, cta_target: e.target.value as "form" | "url" } })}
                                        className={inputCls} style={borderStyle}
                                        data-testid="aktion-cfg-so-cta-target"
                                    >
                                        <option value="form">Zum Formular scrollen</option>
                                        <option value="url">Externe URL</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelCls}>CTA-URL {cfg.special_offer.cta_target !== "url" ? "(nur bei Ziel = URL)" : ""}</label>
                                    <input
                                        value={cfg.special_offer.cta_url}
                                        onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, cta_url: e.target.value } })}
                                        className={inputCls} style={borderStyle}
                                        placeholder="https://…"
                                        disabled={cfg.special_offer.cta_target !== "url"}
                                        data-testid="aktion-cfg-so-cta-url"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Typografie-Sub-Block */}
                        <div className="grid gap-3 mb-3">
                            <SpecialOfferStyleEditor
                                label="Überschrift"
                                style={cfg.special_offer.headline_style}
                                onChange={(next) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, headline_style: next } })}
                                testIdPrefix="aktion-cfg-so-headline"
                            />
                            <SpecialOfferStyleEditor
                                label="Untertitel"
                                style={cfg.special_offer.subline_style}
                                onChange={(next) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, subline_style: next } })}
                                testIdPrefix="aktion-cfg-so-subline"
                            />
                            <SpecialOfferStyleEditor
                                label="CTA-Button"
                                style={cfg.special_offer.cta_style}
                                onChange={(next) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, cta_style: next } })}
                                testIdPrefix="aktion-cfg-so-cta"
                            />
                        </div>

                        {/* Hintergrund-Sub-Block */}
                        <div className="p-3 rounded-sm bg-white" style={borderStyle}>
                            <label className={labelCls}>Sektions-Hintergrund</label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={cfg.special_offer.background_color || "#F6F1E9"}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, background_color: e.target.value } })}
                                    className="w-10 h-8 rounded-sm cursor-pointer"
                                    style={borderStyle}
                                    data-testid="aktion-cfg-so-bg-picker"
                                />
                                <input
                                    type="text"
                                    value={cfg.special_offer.background_color || ""}
                                    onChange={(e) => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, background_color: e.target.value } })}
                                    className={inputCls + " flex-1"}
                                    style={borderStyle}
                                    placeholder="#F6F1E9"
                                    data-testid="aktion-cfg-so-bg-text"
                                />
                                <div className="flex gap-1">
                                    {[
                                        { name: "Cream", color: "#F6F1E9" },
                                        { name: "Weiß", color: "#FFFDF9" },
                                        { name: "Anthrazit", color: "#0F1830" },
                                        { name: "Gold", color: "#D4A855" },
                                    ].map((p) => (
                                        <button
                                            key={p.color}
                                            type="button"
                                            onClick={() => setCfg({ ...cfg, special_offer: { ...cfg.special_offer, background_color: p.color } })}
                                            className="px-2 py-1 text-[10px] rounded-sm hover:opacity-80"
                                            style={{ ...borderStyle, background: p.color, color: p.color === "#0F1830" ? "#fff" : "#0F1830" }}
                                            title={p.name}
                                            data-testid={`aktion-cfg-so-bg-preset-${p.name.toLowerCase()}`}
                                        >
                                            {p.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Legacy: Urgency-Sektion (alt) — nicht mehr aktiv, nur Info. */}
                <details className="mb-6 p-3 rounded-sm text-xs" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }}>
                    <summary className="cursor-pointer text-hf-muted uppercase tracking-[0.22em] text-[10px]">
                        Legacy · Alte Urgency-Sektion (nicht mehr sichtbar auf der Seite)
                    </summary>
                    <div className="mt-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <p className="text-hf-muted mb-2">
                            Ersetzt durch <strong>Aktions-Angebot</strong> oben. Bleibt in der Config für Backward-Compat; nicht mehr auf der Seite gerendert.
                        </p>
                        <label className={labelCls}>Alte Überschrift</label>
                        <input value={cfg.urgency_headline} onChange={(e) => setCfg({ ...cfg, urgency_headline: e.target.value })} className={inputCls} style={borderStyle} />
                    </div>
                </details>

                {/* Gutschein-Popup / Promo-Modal ─────────────────
                 * Konfiguriert das Modal auf `/aktion`:
                 *  - Trigger (Zeit + Scroll-%)
                 *  - Countdown-Modus (Mitternacht / Fixes Datum / Aus)
                 *  - Alle sichtbaren Texte + Rabatt-Prozent
                 *  - Trust-Badges (Array)
                 *  - Formular-Sub-Block (kollabierbar): Labels, Privacy,
                 *    Kampagnen-Tag, Erfolgs-Meldung
                 */}
                <GutscheinPopupEditor
                    cfg={cfg.gutschein_popup}
                    onChange={(next) => setCfg({ ...cfg, gutschein_popup: next })}
                    inputCls={inputCls}
                    labelCls={labelCls}
                    borderStyle={borderStyle}
                />

                {/* Willkommens-Popup · Live-Statistik — Widget mit
                 *  Choice-Verteilung + Konversionsrate. Widget hat einen
                 *  eigenen Range-Filter (unabhängig vom Editor-State), damit
                 *  der Admin schnell zwischen Heute/7d/30d switchen kann,
                 *  ohne den Editor zu tangieren. */}
                <div className="mb-4">
                    <WelcomePopupWidget />
                </div>

                {/* Welcome-Choice-Popup — steuert das Willkommens-Popup auf
                 *  /aktion (2 Wege: 100 % Referenz / 80 % Rabatt). Alle
                 *  sichtbaren Texte + Trigger-Delay + Sperr-Dauer sind hier
                 *  konfigurierbar. */}
                <WelcomeChoicePopupEditor
                    cfg={cfg.welcome_popup}
                    onChange={(next) => setCfg({ ...cfg, welcome_popup: next })}
                    inputCls={inputCls}
                    labelCls={labelCls}
                    borderStyle={borderStyle}
                />

                {/* Referenz-Popup · Texte — steuert das dedizierte Referenz-
                 *  Layout (Gold-Zertifikats-Siegel, ohne Countdown, ohne
                 *  „€"-Wortlaut). Wird beim Klick auf die Referenz-Karte
                 *  im Welcome-Choice-Popup geöffnet. */}
                <ReferenzPopupEditor
                    cfg={cfg.referenz_popup}
                    onChange={(next) => setCfg({ ...cfg, referenz_popup: next })}
                    inputCls={inputCls}
                    labelCls={labelCls}
                    borderStyle={borderStyle}
                />

                {/* Kontaktformular-Sektion — komplett editierbar.
                 * Alle Feld-Labels, Placeholders, Datenschutz, Meldungen und
                 * Typografie unter einem Dach. */}
                <div className="grid gap-4 mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid="aktion-cfg-contact-form">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Kontaktformular (unten auf der Seite)</div>

                    {/* Kopf */}
                    <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Kopf-Bereich</div>
                        <div>
                            <label className={labelCls}>Überschrift</label>
                            <input value={cfg.contact_form.headline} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, headline: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-headline" />
                        </div>
                        <div>
                            <label className={labelCls}>Untertitel</label>
                            <textarea rows={2} value={cfg.contact_form.subline} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, subline: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-subline" />
                        </div>
                    </div>

                    {/* Formular-Felder */}
                    <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Formular-Felder — Labels &amp; Placeholders</div>
                        {[
                            { k: "name", title: "Name" },
                            { k: "plz", title: "PLZ" },
                            { k: "phone", title: "Telefon" },
                            { k: "message", title: "Nachricht (optional)" },
                        ].map(({ k, title }) => (
                            <div key={k} className="grid md:grid-cols-2 gap-3 items-end">
                                <div>
                                    <label className={labelCls}>{title} — Label</label>
                                    <input
                                        value={(cfg.contact_form as unknown as Record<string, string>)[`${k}_label`] || ""}
                                        onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, [`${k}_label`]: e.target.value } as typeof cfg.contact_form })}
                                        className={inputCls}
                                        style={borderStyle}
                                        data-testid={`aktion-cfg-cf-${k}-label`}
                                    />
                                </div>
                                <div>
                                    <label className={labelCls}>{title} — Placeholder</label>
                                    <input
                                        value={(cfg.contact_form as unknown as Record<string, string>)[`${k}_placeholder`] || ""}
                                        onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, [`${k}_placeholder`]: e.target.value } as typeof cfg.contact_form })}
                                        className={inputCls}
                                        style={borderStyle}
                                        data-testid={`aktion-cfg-cf-${k}-placeholder`}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Datenschutz */}
                    <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Datenschutz-Zeile</div>
                        <div>
                            <label className={labelCls}>Text (Platzhalter <code>{"{link}"}</code> wird durch klickbaren Link ersetzt)</label>
                            <textarea rows={2} value={cfg.contact_form.privacy_text} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, privacy_text: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-privacy-text" />
                        </div>
                        <div className="grid md:grid-cols-2 gap-3">
                            <div>
                                <label className={labelCls}>Link-Text</label>
                                <input value={cfg.contact_form.privacy_link_text} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, privacy_link_text: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-privacy-link-text" />
                            </div>
                            <div>
                                <label className={labelCls}>Link-URL</label>
                                <input value={cfg.contact_form.privacy_link_url} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, privacy_link_url: e.target.value } })} className={inputCls} style={borderStyle} placeholder="/datenschutz" data-testid="aktion-cfg-cf-privacy-link-url" />
                            </div>
                        </div>
                    </div>

                    {/* CTA */}
                    <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Submit-Button</div>
                        <div>
                            <label className={labelCls}>Button-Text</label>
                            <input value={cfg.contact_form.submit_text} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, submit_text: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-submit-text" />
                        </div>
                        <div className="grid md:grid-cols-[180px_1fr] gap-3">
                            <div>
                                <label className={labelCls}>Verhalten</label>
                                <select
                                    value={cfg.contact_form.submit_target}
                                    onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, submit_target: e.target.value as "submit" | "url" } })}
                                    className={inputCls}
                                    style={borderStyle}
                                    data-testid="aktion-cfg-cf-submit-target"
                                >
                                    <option value="submit">Formular absenden (Standard)</option>
                                    <option value="url">Externe URL</option>
                                </select>
                            </div>
                            <div>
                                <label className={labelCls}>URL {cfg.contact_form.submit_target !== "url" ? "(nur bei Verhalten = URL)" : ""}</label>
                                <input
                                    value={cfg.contact_form.submit_url || ""}
                                    onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, submit_url: e.target.value } })}
                                    className={inputCls} style={borderStyle}
                                    placeholder="https://…"
                                    disabled={cfg.contact_form.submit_target !== "url"}
                                    data-testid="aktion-cfg-cf-submit-url"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Meldungen */}
                    <div className="grid gap-3 p-3 rounded-sm bg-white" style={borderStyle}>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-hf-muted">Meldungen</div>
                        <div>
                            <label className={labelCls}>Erfolgs-Meldung (nach Absenden)</label>
                            <textarea rows={2} value={cfg.contact_form.success_message} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, success_message: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-success" />
                        </div>
                        <div>
                            <label className={labelCls}>Fehler-Meldung</label>
                            <textarea rows={2} value={cfg.contact_form.error_message} onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, error_message: e.target.value } })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-cf-error" />
                        </div>
                    </div>

                    {/* Typografie */}
                    <div className="grid gap-3">
                        <SpecialOfferStyleEditor
                            label="Überschrift"
                            style={cfg.contact_form.headline_style}
                            onChange={(next) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, headline_style: next } })}
                            testIdPrefix="aktion-cfg-cf-headline"
                        />
                        <SpecialOfferStyleEditor
                            label="Submit-Button"
                            style={cfg.contact_form.submit_style}
                            onChange={(next) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, submit_style: next } })}
                            testIdPrefix="aktion-cfg-cf-submit"
                        />
                    </div>

                    {/* Hintergrund */}
                    <div className="p-3 rounded-sm bg-white" style={borderStyle}>
                        <label className={labelCls}>Sektions-Hintergrund</label>
                        <div className="flex gap-2 items-center">
                            <input
                                type="color"
                                value={cfg.contact_form.background_color || "#FFFDF9"}
                                onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, background_color: e.target.value } })}
                                className="w-10 h-8 rounded-sm cursor-pointer"
                                style={borderStyle}
                                data-testid="aktion-cfg-cf-bg-picker"
                            />
                            <input
                                type="text"
                                value={cfg.contact_form.background_color || ""}
                                onChange={(e) => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, background_color: e.target.value } })}
                                className={inputCls + " flex-1"}
                                style={borderStyle}
                                placeholder="#FFFDF9"
                                data-testid="aktion-cfg-cf-bg-text"
                            />
                            <div className="flex gap-1">
                                {[
                                    { name: "Cream", color: "#F6F1E9" },
                                    { name: "Weiß", color: "#FFFDF9" },
                                    { name: "Anthrazit", color: "#0F1830" },
                                    { name: "Gold", color: "#D4A855" },
                                ].map((p) => (
                                    <button
                                        key={p.color}
                                        type="button"
                                        onClick={() => setCfg({ ...cfg, contact_form: { ...cfg.contact_form, background_color: p.color } })}
                                        className="px-2 py-1 text-[10px] rounded-sm hover:opacity-80"
                                        style={{ ...borderStyle, background: p.color, color: p.color === "#0F1830" ? "#fff" : "#0F1830" }}
                                        title={p.name}
                                        data-testid={`aktion-cfg-cf-bg-preset-${p.name.toLowerCase()}`}
                                    >
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>


                {/* FAQ ─────────────────────────────────────────────
                 * Editor für die FAQ-Sektion auf /aktion. Kicker und Titel
                 * darüber, dann die Frage/Antwort-Liste. Reihenfolge per
                 * Up/Down-Button; Add/Remove pro Zeile. */}
                <div className="mb-6 p-5 rounded-sm" style={{ background: "hsl(var(--hf-surface))", ...borderStyle }} data-testid="aktion-cfg-faq">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">FAQ (Häufige Fragen)</div>
                        <button
                            type="button"
                            onClick={() => setCfg({ ...cfg, faqs: [...cfg.faqs, { q: "", a: "" }] })}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-sm text-xs"
                            style={{ background: "white", ...borderStyle, color: "hsl(var(--hf-ink))" }}
                            data-testid="aktion-cfg-faq-add"
                        >
                            <Plus size={14} /> Frage hinzufügen
                        </button>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 mb-4">
                        <div>
                            <label className={labelCls}>Kicker (klein, über dem Titel)</label>
                            <input value={cfg.faq_kicker} onChange={(e) => setCfg({ ...cfg, faq_kicker: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-faq-kicker" />
                        </div>
                        <div>
                            <label className={labelCls}>Sektions-Titel (groß, Serif)</label>
                            <input value={cfg.faq_title} onChange={(e) => setCfg({ ...cfg, faq_title: e.target.value })} className={inputCls} style={borderStyle} data-testid="aktion-cfg-faq-title" />
                        </div>
                    </div>
                    <div className="grid gap-3">
                        {cfg.faqs.map((f, i) => (
                            <div key={i} className="p-3 rounded-sm bg-white" style={borderStyle} data-testid={`aktion-cfg-faq-row-${i}`}>
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="text-[10px] uppercase tracking-[0.22em] text-hf-muted">Frage {i + 1}</div>
                                    <div className="flex items-center gap-1">
                                        <button
                                            type="button"
                                            aria-label="Nach oben"
                                            disabled={i === 0}
                                            onClick={() => {
                                                const copy = [...cfg.faqs];
                                                [copy[i - 1], copy[i]] = [copy[i], copy[i - 1]];
                                                setCfg({ ...cfg, faqs: copy });
                                            }}
                                            className="p-1 rounded-sm"
                                            style={{ opacity: i === 0 ? 0.35 : 1, cursor: i === 0 ? "not-allowed" : "pointer" }}
                                            data-testid={`aktion-cfg-faq-up-${i}`}
                                        >
                                            <ArrowUp size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Nach unten"
                                            disabled={i === cfg.faqs.length - 1}
                                            onClick={() => {
                                                const copy = [...cfg.faqs];
                                                [copy[i + 1], copy[i]] = [copy[i], copy[i + 1]];
                                                setCfg({ ...cfg, faqs: copy });
                                            }}
                                            className="p-1 rounded-sm"
                                            style={{ opacity: i === cfg.faqs.length - 1 ? 0.35 : 1, cursor: i === cfg.faqs.length - 1 ? "not-allowed" : "pointer" }}
                                            data-testid={`aktion-cfg-faq-down-${i}`}
                                        >
                                            <ArrowDown size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            aria-label="Entfernen"
                                            onClick={() => {
                                                const copy = cfg.faqs.filter((_, idx) => idx !== i);
                                                setCfg({ ...cfg, faqs: copy });
                                            }}
                                            className="p-1 rounded-sm"
                                            style={{ color: "#B91C1C" }}
                                            data-testid={`aktion-cfg-faq-remove-${i}`}
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                                <div className="grid gap-2">
                                    <input
                                        value={f.q}
                                        onChange={(e) => {
                                            const copy = [...cfg.faqs];
                                            copy[i] = { ...f, q: e.target.value };
                                            setCfg({ ...cfg, faqs: copy });
                                        }}
                                        placeholder="Frage"
                                        className={inputCls}
                                        style={borderStyle}
                                        data-testid={`aktion-cfg-faq-q-${i}`}
                                    />
                                    <textarea
                                        value={f.a}
                                        onChange={(e) => {
                                            const copy = [...cfg.faqs];
                                            copy[i] = { ...f, a: e.target.value };
                                            setCfg({ ...cfg, faqs: copy });
                                        }}
                                        rows={3}
                                        placeholder="Antwort"
                                        className={inputCls}
                                        style={borderStyle}
                                        data-testid={`aktion-cfg-faq-a-${i}`}
                                    />
                                </div>
                            </div>
                        ))}
                        {cfg.faqs.length === 0 && (
                            <div className="text-sm text-hf-muted italic p-3">
                                Noch keine Fragen — klicke oben rechts auf &bdquo;Frage hinzuf&uuml;gen&ldquo;.
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="p-4 rounded-sm mb-4 text-sm" style={{ background: "hsl(0 50% 96%)", border: "1px solid hsl(0 30% 80%)", color: "hsl(0 30% 30%)" }}>
                        {error}
                    </div>
                )}

                {/* Save-Button */}
                <div className="sticky bottom-4 z-10">
                    <div
                        className="p-4 rounded-sm flex items-center justify-between gap-3"
                        style={{ background: "white", ...borderStyle, boxShadow: "0 6px 20px -8px rgba(0,0,0,0.2)" }}
                    >
                        <div className="text-sm text-hf-muted">
                            Änderungen wirken erst, wenn du sie speicherst.
                        </div>
                        <button
                            type="button"
                            onClick={saveCfg}
                            disabled={cfgStatus === "saving"}
                            className="inline-flex items-center gap-2 px-5 py-2 rounded-sm text-sm"
                            style={{ background: "#C48B34", color: "white", border: "1px solid #8A5A1D", fontWeight: 600 }}
                            data-testid="aktion-cfg-save"
                        >
                            <Save size={16} strokeWidth={2} />
                            {cfgStatus === "saving" ? "Speichere…" : cfgStatus === "saved" ? "✓ Gespeichert" : "Alle Änderungen speichern"}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
}
